/**
 * Detects spam/inappropriate content in user-submitted text.
 * Used client-side in form components and server-side in API routes.
 *
 * Blocks: excessive URLs (>3), HTML injection, explicit/spam keywords.
 * Note: http/https/www are covered by the URL count; href by the HTML check.
 */
export function isSpamContent(text) {
  if (!text) return false;
  const urlCount  = (text.match(/https?:\/\/|www\./gi) ?? []).length;
  const hasScript = /<script|javascript:|on\w+\s*=|href\s*=/i.test(text);
  const spamWords = /\b(viagra|casino|lottery|winner|click here|free money|make money fast|earn \$|buy now|limited offer|porn|sex|sexy|pornhub|xxx|gay|lesbian|hardcorexxx|porno|pornography|porntube|fuck|pussy|vidio|fucking|hot|tiktok)\b/i.test(text);
  return urlCount > 3 || hasScript || spamWords;
}

/**
 * Validates the contact / enquiry form.
 *
 * @param {{ form: object, code: string }} params
 * @returns {Record<string, string>} — empty object means no errors
 */
export function validateContactForm({ form, code }) {
  const errors = {};

  // Name — required, 2–60 chars, letters/spaces/hyphens/apostrophes only
  const name = form.name?.trim() ?? "";
  if (!name) {
    errors.name = "Name should not be empty.";
  } else if (name.length < 2) {
    errors.name = "Name must be at least 2 characters.";
  } else if (name.length > 60) {
    errors.name = "Name must be 60 characters or fewer.";
  } else if (!/^[a-zA-Z\s'\-]+$/.test(name)) {
    errors.name = "Name can only contain letters, spaces, hyphens, and apostrophes.";
  }

  // Phone — required, 7–15 digits (allows +, spaces, hyphens, parentheses)
  const phone = form.phone?.trim() ?? "";
  if (!phone) {
    errors.phone = "Phone number should not be empty.";
  } else if (!/^[\+\d\s\-\(\)\.]+$/.test(phone)) {
    errors.phone = "Enter a valid phone number.";
  } else {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 5 || digits.length > 30) {
      errors.phone = "Phone number must be between 5 and 30 digits.";
    }
  }

  // Email — required, valid format
  const email = form.email?.trim() ?? "";
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!email) {
    errors.email = "Email should not be empty.";
  } else if (!emailRe.test(email)) {
    errors.email = "Enter a valid email address.";
  }

  // Message — required, min 10 chars
  const message = form.message?.trim() ?? "";
  if (!message) {
    errors.message = "Message should not be empty.";
  } else if (message.length < 10) {
    errors.message = "Message must be at least 10 characters.";
  }

  // Captcha
  if (!form.captcha?.trim()) {
    errors.captcha = "Captcha should not be empty.";
  } else if (form.captcha.trim().toUpperCase() !== code.toUpperCase()) {
    errors.captcha = "Incorrect code. Try again.";
  }

  return errors;
}
